import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { contractConfigApi, type PingPayout } from '../services/api';
import { PingPayoutAmounts } from '../components/PingPayoutPanel';
import { PingPayoutStatus } from '../components/PingPayoutStatus';

export function PingPayoutDebugBank() {
  const { payoutId } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [payout, setPayout] = useState<PingPayout | null>(null);
  const [error, setError] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const load = useCallback(async () => {
    if (!payoutId) return;
    setError(false);
    try { setPayout(await contractConfigApi.getPingPayoutDebugBank(payoutId)); setUncertain(false); }
    catch { setError(true); }
  }, [payoutId]);
  useEffect(() => { void load(); }, [load]);
  const act = async (action: 'approve' | 'disburse') => {
    if (!payoutId || !payout?.debugBankAvailable || inFlight.current || uncertain) return;
    inFlight.current = true; setBusy(true); setError(false);
    try { setPayout(await contractConfigApi.actPingPayoutDebugBank(payoutId, action)); }
    catch { setUncertain(true); }
    finally { inFlight.current = false; setBusy(false); }
  };
  const base = user?.personaType === 'person' ? '/person/projects' : user?.isAdmin ? '/admin/projects' : '/projects';
  const back = payout ? `${base}/${payout.projectId}?${new URLSearchParams({ tab: 'contract', contractId: payout.contractId })}` : '/home';
  return <main className="min-vh-100 bg-light p-3 p-sm-4">
    <div className="container py-3 py-sm-5"><div className="row justify-content-center"><div className="col-12 col-md-9 col-lg-7">
      <section className="card border-warning shadow-sm text-break" aria-labelledby="payout-bank-title">
        <header className="card-header bg-warning text-dark p-3 d-flex flex-column align-items-start">
          <span className="badge bg-dark mb-2">{t('debugBank.badge')}</span>
          <h1 id="payout-bank-title" className="h3 mb-0 w-100">{t('pingPayout.bankTitle')}</h1>
        </header>
        <div className="card-body p-3 p-sm-4">
          <p>{t('pingPayout.bankExplanation')}</p>
          {error && <p className="alert alert-danger" role="alert">{t('pingPayout.loadError')}</p>}
          {uncertain && <p className="alert alert-warning" role="alert">{t('pingPayout.uncertain')}</p>}
          {payout && <>
            <PingPayoutStatus payout={payout} canUseDebugBank showDebugBankLink={false} />
            <PingPayoutAmounts payout={payout} />
            {payout.companyAmountOre === 0 && <p>{t('pingPayout.feeOnly')}</p>}
            {(payout.mode === 'mock' || user?.isAdmin || payout.state !== 'waiting_approval') && !/^ping_(company|fee)_(account_|kyc_)/.test(payout.errorCode ?? '') && payout.debugBankAvailable && ['waiting_approval', 'awaiting_payout'].includes(payout.state) && <button type="button" className="btn btn-primary btn-lg w-100 my-3" disabled={busy || uncertain} onClick={() => void act(payout.state === 'waiting_approval' ? 'approve' : 'disburse')}>
              {t(busy ? 'pingPayout.working' : payout.state === 'waiting_approval' ? 'pingPayout.approve' : 'pingPayout.disburse')}
            </button>}
          </>}
          <div className="d-grid gap-3 mt-3">
            <button type="button" className="btn btn-outline-primary btn-lg" disabled={busy} onClick={() => void load()}>{t('pingPayout.refresh')}</button>
            <Link className="btn btn-outline-secondary btn-lg" to={back}>{t('paymentCheckout.backToProject')}</Link>
          </div>
        </div>
      </section>
    </div></div></div>
  </main>;
}
